import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import type { Shipment, ShipmentDetail, ShipmentPage, ShipmentTab } from './types';

import { ApiError, apiRequest } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * The « Expéditions » screen's calls (`/api/v1/merchant/deliveries…`). Every action goes through OUR API, which calls
 * Jibex with the merchant's own sealed credentials — the browser never talks to the carrier.
 */

const KEY = 'shipments';

export function useShipmentsQuery(
  tab: ShipmentTab,
  page: number,
): UseQueryResult<ShipmentPage, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: [KEY, activeShopId, tab, page],
    queryFn: () =>
      apiRequest<ShipmentPage>(`/merchant/deliveries?tab=${tab}&page=${String(page)}&pageSize=20`, {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null,
  });
}

export function useShipmentDetailQuery(
  id: string | null,
): UseQueryResult<ShipmentDetail, ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: [KEY, activeShopId, 'detail', id],
    queryFn: () =>
      apiRequest<ShipmentDetail>(`/merchant/deliveries/${id ?? ''}`, {
        accessToken,
        shopId: activeShopId,
      }),
    enabled: accessToken !== null && activeShopId !== null && id !== null,
  });
}

export function useServedGovernoratesQuery(
  enabled: boolean,
): UseQueryResult<readonly string[], ApiError> {
  const { accessToken, activeShopId } = useSession();
  return useQuery({
    queryKey: [KEY, activeShopId, 'governorates'],
    queryFn: async () =>
      (
        await apiRequest<{ governorates: readonly string[] }>('/merchant/deliveries/governorates', {
          accessToken,
          shopId: activeShopId,
        })
      ).governorates,
    enabled: enabled && accessToken !== null && activeShopId !== null,
    staleTime: 60 * 60_000,
    retry: false,
  });
}

export type ShipmentAction = 'refresh' | 'retry' | 'cancel' | 'resend';

/** One action on one shipment. Everything about shipments (and orders, whose status may move) is refreshed after. */
export function useShipmentActionMutation(): UseMutationResult<
  unknown,
  ApiError,
  { readonly id: string; readonly action: ShipmentAction }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }) =>
      apiRequest(`/merchant/deliveries/${id}/${action}`, {
        method: 'POST',
        accessToken,
        shopId: activeShopId,
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [KEY] });
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

/** Period of the automatic status refresh — also the API's own per-parcel cooldown (`REFRESH_COOLDOWN_SECONDS`). */
export const AUTO_REFRESH_MS = 60_000;

/**
 * « Actualisation automatique » of one parcel's status while its detail is open: every 60 s, the SAME call as the
 * « Actualiser » button (`POST /merchant/deliveries/{id}/refresh` — our API asks Jibex, which stays the source of
 * truth), then the screens showing it are refetched, so the new status appears without any click or reload.
 *
 * - ONE timer per open parcel: a chain of `setTimeout`s re-armed only after each attempt has finished, so calls can
 *   neither pile up nor overlap; cleared when the drawer closes, the parcel changes, the shop changes or the merchant
 *   signs out (`enabled` / `accessToken`), and re-armed when they come back.
 * - Never two refreshes at once: a tick is skipped while one is in flight — automatic or the manual button
 *   (`manualBusy`) — and the 60 s restart after a manual refresh, which the API's cooldown would otherwise refuse.
 * - Silent on failure: a network error or Jibex being down shows nothing and is retried at the next cycle; a 429 (the
 *   cooldown) waits for its `Retry-After`. Never a reason to sign out — an expired token is renewed by `apiRequest`.
 * - Paused while the tab is hidden (no carrier calls for nobody), resumed by the next tick once visible.
 */
export function useShipmentAutoRefresh(
  id: string | null,
  enabled: boolean,
  manualBusy: boolean,
): { readonly refreshing: boolean; readonly lastRefreshedAt: Date | null } {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const tokenRef = useRef(accessToken);
  const manualBusyRef = useRef(manualBusy);
  const restartRef = useRef<(() => void) | null>(null);
  const signedIn = accessToken !== null;

  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    const wasBusy = manualBusyRef.current;
    manualBusyRef.current = manualBusy;
    // A manual refresh just finished: the next automatic one is 60 s after it.
    if (wasBusy && !manualBusy) restartRef.current?.();
  }, [manualBusy]);

  useEffect(() => {
    setLastRefreshedAt(null);
    if (!enabled || id === null || activeShopId === null || !signedIn) return undefined;
    const shopId = activeShopId;
    // Read through a function: the cleanup flips the flag while an attempt is awaiting.
    const life = { stopped: false };
    const isStopped = (): boolean => life.stopped;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | null = null;

    const arm = (delay: number): void => {
      clearTimeout(timer);
      timer = setTimeout(() => void tick(), delay);
    };
    const tick = async (): Promise<void> => {
      if (isStopped()) return;
      if (inFlight || manualBusyRef.current || document.visibilityState !== 'visible') {
        arm(AUTO_REFRESH_MS);
        return;
      }
      inFlight = true;
      setRefreshing(true);
      controller = new AbortController();
      let next = AUTO_REFRESH_MS;
      try {
        await apiRequest(`/merchant/deliveries/${id}/refresh`, {
          method: 'POST',
          accessToken: tokenRef.current,
          shopId,
          signal: controller.signal,
        });
        if (isStopped()) return;
        setLastRefreshedAt(new Date());
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [KEY, shopId] }),
          queryClient.invalidateQueries({ queryKey: ['orders', shopId] }),
        ]);
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.status === 429 &&
          error.retryAfterSeconds !== undefined
        ) {
          next = (error.retryAfterSeconds + 1) * 1000;
        }
        // Anything else: silent, the next cycle tries again.
      } finally {
        inFlight = false;
        controller = null;
        if (!isStopped()) {
          setRefreshing(false);
          arm(next);
        }
      }
    };

    restartRef.current = () => {
      if (!inFlight) arm(AUTO_REFRESH_MS);
    };
    arm(AUTO_REFRESH_MS);
    return () => {
      life.stopped = true;
      clearTimeout(timer);
      controller?.abort();
      restartRef.current = null;
      setRefreshing(false);
    };
  }, [enabled, id, activeShopId, signedIn, queryClient]);

  return { refreshing, lastRefreshedAt };
}

export function useShipmentCityMutation(): UseMutationResult<
  { readonly shipment: Shipment },
  ApiError,
  { readonly id: string; readonly city: string | null }
> {
  const { accessToken, activeShopId } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, city }) =>
      apiRequest<{ shipment: Shipment }>(`/merchant/deliveries/${id}/city`, {
        method: 'PUT',
        body: { city },
        accessToken,
        shopId: activeShopId,
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}
